import axios from "axios";
import { Base_URL } from "../url";

// Create axios instance with interceptors for debugging
const api = axios.create({
  baseURL: Base_URL,
  timeout: 30000,
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.group(`🚀 ${config.method?.toUpperCase()} ${config.url}`);
    console.log('📦 Headers:', config.headers);
    console.log('🔧 Config:', config);
    
    if (config.data instanceof FormData) {
      console.log('📁 FormData contents:');
      for (let pair of config.data.entries()) {
        if (pair[1] instanceof File) {
          console.log(`  ${pair[0]}:`, {
            name: pair[1].name,
            size: pair[1].size,
            type: pair[1].type
          });
        } else {
          console.log(`  ${pair[0]}:`, pair[1]);
        }
      }
    } else {
      console.log('📝 Data:', config.data);
    }
    
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('✅ Response:', {
      status: response.status,
      data: response.data
    });
    console.groupEnd();
    return response;
  },
  (error) => {
    console.error('❌ Response error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message,
      config: error.config
    });
    console.groupEnd();
    return Promise.reject(error);
  }
);

export const documentService = {
  // Upload ID/Bank document
  async uploadDocument(userId, documentType, file) {
    console.group('📤 Document Upload Service');
    
    // Get user details from localStorage
    let organisationId = null;
    const userDetailsStr = localStorage.getItem('userDetails');
    
    if (userDetailsStr) {
      try {
        const userDetails = JSON.parse(userDetailsStr);
        organisationId = userDetails.organisationId;
      } catch (error) {
        console.error('❌ Error parsing userDetails:', error);
      }
    }
    
    if (!organisationId) {
      console.error('❌ No organisationId found in localStorage');
      throw new Error('Organisation ID not found. Please log in again.');
    }
    
    // Build FormData
    const formData = new FormData();
    formData.append('File', file);
    formData.append('UserId', userId.toString());
    formData.append('OrganisationId', organisationId.toString());
    formData.append('DocumentType', documentType);
    
    try {
      const response = await api.post('/document/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ Upload successful! Response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Upload failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Delete document
  async deleteDocument(documentId) {
    console.group('🗑️ Delete Document');
    
    try {
      const response = await api.delete(`/document/${documentId}`);
      console.log('✅ Delete successful! Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Delete failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get user documents
  async getUserDocuments(userId) {
    console.group('📋 Get User Documents');
    
    try {
      const response = await api.get(`/document/user/${userId}`);
      console.log('✅ Get documents response:', response.data);
      
      if (response.data.success && Array.isArray(response.data.documents)) {
        return { success: true, documents: response.data.documents };
      } else if (Array.isArray(response.data)) {
        return { success: true, documents: response.data };
      } else if (Array.isArray(response.data?.data)) {
        return { success: true, documents: response.data.data };
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        return { success: false, documents: [] };
      }
    } catch (error) {
      console.error('❌ Get documents failed:', error);
      return { success: false, documents: [] };
    } finally {
      console.groupEnd();
    }
  },

  // View document (opens in new tab)
  async viewDocument(documentId) {
    console.group('👁️ View Document');
    
    try {
      const response = await api.get(`/document/view/${documentId}`);
      
      if (response.data.success && response.data.url) {
        console.log('✅ Opening document URL:', response.data.url);
        window.open(response.data.url, '_blank');
        return { success: true, url: response.data.url };
      } else {
        console.error('❌ No URL found in response');
        throw new Error('Document URL not found');
      }
    } catch (error) {
      console.error('❌ View document failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get document types
  async getDocumentTypes() {
    console.group('📋 Get Document Types');
    
    try {
      const response = await api.get('/document/types');
      console.log('✅ Get document types successful! Found:', response.data.length || 0);
      return response.data;
    } catch (error) {
      console.error('❌ Get document types failed:', error);
      return [];
    } finally {
      console.groupEnd();
    }
  }
};

export const qualificationService = {
  // Upload multiple qualifications - FIXED: Removed companyCode parameter
  async uploadQualificationsBulk(qualifications) {
    console.group('📤 Bulk Qualifications Upload');
    
    // Get user details from localStorage
    let userId = null;
    let organisationId = null;
    const userDetailsStr = localStorage.getItem('userDetails');
    
    if (userDetailsStr) {
      try {
        const userDetails = JSON.parse(userDetailsStr);
        userId = userDetails.userId;
        organisationId = userDetails.organisationId;
      } catch (error) {
        console.error('❌ Error parsing userDetails:', error);
      }
    }
    
    if (!userId || !organisationId) {
      console.error('❌ Missing user details:', { userId, organisationId });
      throw new Error('User information not found. Please log in again.');
    }
    
    // Validate qualifications
    const validQualifications = qualifications.filter((qual, index) => {
      if (!qual.qualificationType || !qual.institute || !qual.year || !qual.attachment) {
        console.warn(`⚠️ Qualification ${index} missing required fields`);
        return false;
      }
      return true;
    });
    
    console.log(`📊 Valid qualifications: ${validQualifications.length}/${qualifications.length}`);
    
    if (validQualifications.length === 0) {
      throw new Error('Please fill all required fields and upload files for at least one qualification.');
    }
    
    // Build FormData
    const formData = new FormData();
    formData.append('UserId', userId.toString());
    formData.append('OrganisationId', organisationId.toString());
    
    // Add each qualification
    validQualifications.forEach((qual, index) => {
      formData.append(`Qualifications[${index}].QualificationType`, qual.qualificationType);
      formData.append(`Qualifications[${index}].Institute`, qual.institute);
      formData.append(`Qualifications[${index}].Year`, qual.year.toString());
      formData.append(`Qualifications[${index}].Grade`, qual.grade || '');
      
      if (qual.attachment) {
        formData.append(`Qualifications[${index}].File`, qual.attachment);
      }
    });
    
    try {
      const response = await api.post('/qualification/upload-bulk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ Bulk upload successful! Response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Bulk upload failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Upload single qualification
  async uploadQualification(qualificationData) {
    console.group('📤 Single Qualification Upload');
    
    // Get user details from localStorage
    let userId = null;
    let organisationId = null;
    const userDetailsStr = localStorage.getItem('userDetails');
    
    if (userDetailsStr) {
      try {
        const userDetails = JSON.parse(userDetailsStr);
        userId = userDetails.userId;
        organisationId = userDetails.organisationId;
      } catch (error) {
        console.error('❌ Error parsing userDetails:', error);
      }
    }
    
    if (!userId || !organisationId) {
      console.error('❌ Missing user details:', { userId, organisationId });
      throw new Error('User information not found. Please log in again.');
    }
    
    // Validate required fields
    if (!qualificationData.qualificationType || !qualificationData.institute || 
        !qualificationData.year || !qualificationData.file) {
      throw new Error('Please fill all required fields and upload a file.');
    }
    
    // Build FormData for single upload
    const formData = new FormData();
    formData.append('UserId', userId.toString());
    formData.append('OrganisationId', organisationId.toString());
    formData.append('QualificationType', qualificationData.qualificationType);
    formData.append('Institute', qualificationData.institute);
    formData.append('Year', qualificationData.year.toString());
    formData.append('Grade', qualificationData.grade || '');
    formData.append('File', qualificationData.file);
    
    try {
      const response = await api.post('/qualification/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ Single qualification upload successful! Response:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Single qualification upload failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get user qualifications
  async getUserQualifications(userId) {
    console.group('📋 Get User Qualifications');
    
    try {
      const response = await api.get(`/qualification/user/${userId}`);
      console.log('✅ Get qualifications successful! Found:', response.data.qualifications?.length || 0);
      
      if (response.data.success && Array.isArray(response.data.qualifications)) {
        return { success: true, qualifications: response.data.qualifications };
      } else if (Array.isArray(response.data)) {
        return { success: true, qualifications: response.data };
      } else {
        return { success: false, qualifications: [] };
      }
    } catch (error) {
      console.error('❌ Get qualifications failed:', error);
      return { success: false, qualifications: [] };
    } finally {
      console.groupEnd();
    }
  },

  // Delete qualification
  async deleteQualification(qualificationId) {
    console.group('🗑️ Delete Qualification');
    
    try {
      const response = await api.delete(`/qualification/${qualificationId}`);
      console.log('✅ Delete successful! Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Delete failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Get qualification by ID
  async getQualificationById(qualificationId) {
    console.group('🔍 Get Qualification By ID');
    
    try {
      const response = await api.get(`/qualification/${qualificationId}`);
      console.log('✅ Get qualification successful!', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Get qualification failed:', error);
      return { success: false, message: 'Qualification not found' };
    } finally {
      console.groupEnd();
    }
  }
};

export const instituteService = {
  // Get all institutes
  async getInstitutes() {
    console.group('📋 Get Institutes');
    
    try {
      const response = await api.get('/Institute/GetInstituteList');
      console.log('✅ Get institutes successful! Raw response:', response.data);
      
      let institutes = [];
      
      if (Array.isArray(response.data)) {
        institutes = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        institutes = response.data.data;
      } else if (response.data?.institutes && Array.isArray(response.data.institutes)) {
        institutes = response.data.institutes;
      } else if (typeof response.data === 'object') {
        institutes = Object.values(response.data).filter(item => 
          typeof item === 'object' && (item.instituteName || item.name)
        );
      }
      
      // Transform to expected format
      const transformed = institutes.map((item, index) => ({
        instId: item.instituteId || item.id || item.instId || index + 1,
        instName: item.instituteName || item.name || item.instName || 'Unknown Institute'
      }));
      
      console.log(`📊 Transformed ${transformed.length} institutes:`, transformed);
      return transformed;
      
    } catch (error) {
      console.error('❌ Get institutes failed:', error);
      return [];
    } finally {
      console.groupEnd();
    }
  },

  // Add new institute
  async addInstitute(instituteName) {
    console.group('➕ Add Institute');
    
    // Get user details from localStorage
    const userDetailsStr = localStorage.getItem('userDetails');
    let userId = null;
    let username = null;
    
    if (userDetailsStr) {
      try {
        const userDetails = JSON.parse(userDetailsStr);
        userId = userDetails.userId;
        username = userDetails.username || 'Unknown';
      } catch (error) {
        console.error('❌ Error parsing userDetails:', error);
      }
    }
    
    try {
      // Get current timezone from browser (truncate to 10 chars)
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const shortTimeZone = timeZone.length > 10 ? timeZone.substring(0, 10) : timeZone;
      
      const response = await api.post('/Institute/PostInstituteList', {
        instituteName: instituteName,
        createdBy: userId?.toString() || username || 'System',  // Send userId or username
        createdTimezone: shortTimeZone  // Send truncated timezone
      });
      
      console.log('✅ Add institute successful! Response:', response.data);
      
      return {
        instId: response.data?.instituteId || response.data?.InstituteId || Date.now(),
        instName: instituteName
      };
      
    } catch (error) {
      console.error('❌ Add institute failed:', error);
      throw error;
    } finally {
      console.groupEnd();
    }
  },

  // Search institutes
  async searchInstitutes(searchTerm) {
    console.group('🔍 Search Institutes');
    
    try {
      const allInstitutes = await this.getInstitutes();
      
      if (!searchTerm || searchTerm.trim() === '') {
        return allInstitutes;
      }
      
      const searchLower = searchTerm.toLowerCase();
      const filtered = allInstitutes.filter(institute =>
        institute.instName?.toLowerCase().includes(searchLower)
      );
      
      console.log(`📊 Found ${filtered.length} institutes matching "${searchTerm}"`);
      return filtered;
      
    } catch (error) {
      console.error('❌ Search institutes failed:', error);
      return [];
    } finally {
      console.groupEnd();
    }
  }
};